import { useContext } from "react";
import {
  LinjeFiltersContext,
  LinjeFiltersEventContext,
} from "../../../../context/FiltersContext";
import { useHotels } from "../../../../hooks/useQueries";
import {
  QueryFilter,
  LinjeFilterKeys,
  LinjeHotelFilterSetting,
} from "../../../../interfaces/filters";
import { FmulinjeDtoFilterInput } from "../../../../interfaces/schema";
import { f } from "../../../../util/f";
import { AccordionFilterItem } from "../../../accordion/Accordion";
import DefaultDropdownItem from "../../../dropdown/DefaultDropdownItem";
import SuperDropdown from "../../../dropdown/SuperDropdown";
import AppliedFilterItem from "../../../filter/AppliedFilterItem";
import SearchIcon from "../../../icons/SearchIcon";
import { orderBy } from "../../../../util/orderBy";

// helpers
const filterKey: QueryFilter<LinjeFilterKeys>["filterKey"] = "hotel";

export default function LinjeHotelFilter() {
  // context
  const filters = useContext(LinjeFiltersContext);
  const updateFilters = useContext(LinjeFiltersEventContext);

  // // find active settings
  const settings: LinjeHotelFilterSetting[] =
    filters.find((f) => f.filterKey === filterKey)?.settings || [];

  // hooks
  const { hotels } = useHotels();

  // sortings
  const sortedHotels = orderBy(hotels, ["navn1", "sted"]);

  // handlers
  function updateFilterHandler(selectedItem: { id: string; name: string }) {
    // check if selected element already is in settings otherwise add it
    if (!settings?.some((s) => s.id === selectedItem.id)) {
      settings.push({ id: selectedItem.id, name: selectedItem.name });
    }

    const filterString = f<FmulinjeDtoFilterInput>()
      .addFilter(
        "or",
        settings.map((set) => {
          return {
            hotelId: {
              eq: set.id,
            },
          };
        }),
      )
      .build();

    const queryFilter: QueryFilter<LinjeFilterKeys, LinjeHotelFilterSetting[]> =
      {
        filter: filterString,
        filterKey: filterKey,
        settings: settings,
        parentFilter: "and",
      };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  function removeFilterHandler(id: LinjeHotelFilterSetting["id"]) {
    const updatedSettings: LinjeHotelFilterSetting[] =
      settings.filter((set) => {
        return id !== set.id;
      }) || [];

    const filterString = f<FmulinjeDtoFilterInput>()
      .addFilter(
        "or",
        updatedSettings.map((set) => {
          return {
            hotelId: {
              eq: set.id,
            },
          };
        }),
      )
      .build();

    const update = updatedSettings.length > 0;

    if (update) {
      updateFilters({
        event: "update",
        queryFilter: {
          filterKey: filterKey,
          filter: filterString,
          settings: updatedSettings,
        },
      });
    } else {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
    }
  }

  return (
    <AccordionFilterItem
      title="Hotel"
      value="hotel"
      isActive={!!settings.length}
    >
      {settings && (
        <div className="c-applied-filter-group">
          {settings?.map((filter) => (
            <AppliedFilterItem
              key={filter.id}
              label={filter.name}
              removeFilterHandler={() => removeFilterHandler(filter.id)}
            />
          ))}
        </div>
      )}

      <SuperDropdown
        items={sortedHotels}
        itemsIdKey="id"
        itemToString={(item) => item?.navn1 ?? ""}
        itemToElement={(item) => (
          <DefaultDropdownItem
            label={`${item.navn1}`}
            subLabel={item.sted ?? "Ingen adresse"}
          />
        )}
        filterKeys={["navn1"]}
        placeholder="Søg efter hotel ..."
        clearInputAfterSelection={true}
        closedIcon={<SearchIcon height={14} />}
        openIcon={<SearchIcon height={14} />}
        onSelectedItemEvent={(item) => {
          if (item.selectedItem?.id && item.selectedItem.navn1) {
            updateFilterHandler({
              id: item.selectedItem.id,
              name: item.selectedItem.navn1,
            });
          }
        }}
      />
    </AccordionFilterItem>
  );
}
